import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { VoiceCallProvider } from "@/contexts/VoiceCallContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Activate from "@/pages/Activate";
import AuthCallback from "@/pages/AuthCallback";
import CompleteProfile from "@/pages/CompleteProfile";
import Profile from "@/pages/customer/Profile";
import WhatsAppSupport from "@/components/WhatsAppSupport";
import InstallAppBanner from "@/components/InstallAppBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import MyPayReturn from "@/pages/customer/MyPayReturn";
import MyPayOrderReturn from "@/pages/customer/MyPayOrderReturn";
import RestaurantsList from "@/pages/customer/RestaurantsList";
import RestaurantMenu from "@/pages/customer/RestaurantMenu";
import Cart from "@/pages/customer/Cart";
import Checkout from "@/pages/customer/Checkout";
import Tracking from "@/pages/customer/Tracking";
import Orders from "@/pages/customer/Orders";
import DriverDashboard from "@/pages/driver/Dashboard";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminBranches from "@/pages/admin/Branches";
import AdminMenu from "@/pages/admin/Menu";
import AdminLiveMap from "@/pages/admin/LiveMap";
import AdminPayments from "@/pages/admin/Payments";
import AdminMyPay from "@/pages/admin/MyPay";
import AdminOrders from "@/pages/admin/Orders";
import AdminCallRecordings from "@/pages/admin/CallRecordings";
import AdminShopProfile from "@/pages/admin/ShopProfile";
import RestaurantDashboard from "@/pages/restaurant/Dashboard";
import PrinterSetup from "@/pages/restaurant/PrinterSetup";
import DownloadApp from "@/pages/DownloadApp";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center gradient-warm">
      <div className="flex flex-col items-center gap-3">
        <img src="/icon-192.png" alt="طلبية" className="w-16 h-16 rounded-2xl object-cover animate-pulse-dot" style={{boxShadow:"0 4px 12px rgba(37,71,208,0.35)"}} />
        <div className="text-sm text-[var(--text-secondary)]">جاري التحميل...</div>
      </div>
    </div>
  );
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen/>;
  if (!user) return <Navigate to="/login" replace/>;
  if (role && user.role !== role) {
    if (user.role === "driver") return <Navigate to="/driver" replace/>;
    if (user.role === "admin") return <Navigate to="/admin" replace/>;
    if (user.role === "restaurant") return <Navigate to="/restaurant" replace/>;
    return <Navigate to="/" replace/>;
  }
  return children;
}

/** Global overlay: shows the WhatsApp support widget on every customer-facing
 * screen (logged-in customer OR anonymous browsing at "/"). Rendered once at
 * the App root so it's never re-mounted between route changes. */
function GlobalCustomerOverlay() {
  const { user } = useAuth();
  const showWidget = !user || user.role === "customer";
  if (!showWidget) return null;
  return <WhatsAppSupport/>;
}

function RoleRoot() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen/>;
  if (user?.role === "driver") return <Navigate to="/driver" replace/>;
  if (user?.role === "admin") return <Navigate to="/admin" replace/>;
  if (user?.role === "restaurant") return <Navigate to="/restaurant" replace/>;
  // Logged-in customer → shop list. Anonymous visitor → welcome page.
  if (user) return <RestaurantsList/>;
  // Anonymous — allow guest browsing via a persistent flag they can set from Landing.
  if (typeof window !== "undefined" && sessionStorage.getItem("talabya:guest-browse") === "1") {
    return <RestaurantsList/>;
  }
  return <Navigate to="/welcome" replace/>;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <CartProvider>
              <VoiceCallProvider>
                <Toaster position="top-center" richColors/>
                <InstallAppBanner/>
                <AnnouncementBanner/>
                <GlobalCustomerOverlay/>
                <Routes>
                  <Route path="/welcome" element={<Landing/>}/>
                  <Route path="/login" element={<Login/>}/>
                  <Route path="/register" element={<Register/>}/>
                  <Route path="/activate" element={<Activate/>}/>
                  <Route path="/auth-callback" element={<AuthCallback/>}/>
                  <Route path="/complete-profile" element={<Protected><CompleteProfile/></Protected>}/>
                  <Route path="/wallet" element={<Navigate to="/profile" replace/>}/>
                  <Route path="/wallet/mypay/return" element={<Protected role="customer"><MyPayReturn/></Protected>}/>
                  <Route path="/wallet/mypay/order-return" element={<Protected role="customer"><MyPayOrderReturn/></Protected>}/>
                  <Route path="/profile" element={<Protected role="customer"><Profile/></Protected>}/>

                  <Route path="/" element={<RoleRoot/>}/>
                  <Route path="/r/:slug" element={<RestaurantMenu/>}/>
                  <Route path="/cart" element={<Protected role="customer"><Cart/></Protected>}/>
                  <Route path="/checkout" element={<Protected role="customer"><Checkout/></Protected>}/>
                  <Route path="/orders" element={<Protected role="customer"><Orders/></Protected>}/>
                  <Route path="/track/:id" element={<Protected><Tracking/></Protected>}/>

                  <Route path="/driver" element={<Protected role="driver"><DriverDashboard/></Protected>}/>
                  <Route path="/restaurant" element={<Protected role="restaurant"><RestaurantDashboard/></Protected>}/>
                  <Route path="/printer-setup" element={<Protected role="restaurant"><PrinterSetup/></Protected>}/>
                  <Route path="/admin" element={<Protected role="admin"><AdminDashboard/></Protected>}/>
                  <Route path="/admin/orders" element={<Protected role="admin"><AdminOrders/></Protected>}/>
                  <Route path="/admin/restaurants" element={<Protected role="admin"><AdminBranches/></Protected>}/>
                  <Route path="/admin/branches" element={<Protected role="admin"><AdminBranches/></Protected>}/>
                  <Route path="/admin/menu" element={<Protected role="admin"><AdminMenu/></Protected>}/>
                  <Route path="/admin/live-map" element={<Protected role="admin"><AdminLiveMap/></Protected>}/>
                  <Route path="/admin/payments" element={<Protected role="admin"><AdminPayments/></Protected>}/>
                  <Route path="/admin/mypay" element={<Protected role="admin"><AdminMyPay/></Protected>}/>
                  <Route path="/admin/call-recordings" element={<Protected role="admin"><AdminCallRecordings/></Protected>}/>
                  <Route path="/admin/shops/:id" element={<Protected role="admin"><AdminShopProfile/></Protected>}/>
                  <Route path="/download-app" element={<DownloadApp/>}/>

                  <Route path="*" element={<Navigate to="/" replace/>}/>
                </Routes>
              </VoiceCallProvider>
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
